import React, { lazy } from 'react';
import { ArrowRight, Scale, Calculator, HardHat, HeartPulse } from 'lucide-react';
import { Section, SectionHeading } from '../ui/Section';
import { IMAGES } from '../../lib/constants';
const primary = [{
  name: 'Legal',
  image: IMAGES.legal,
  alt: 'Lawyers reviewing documents together in a modern law firm',
  pain: 'Practice management, document workflows and matter systems cannot afford downtime or unclear ownership.',
  outcome: 'Technology support designed around client confidentiality, matter systems and billable-time continuity.'
}, {
  name: 'Accounting',
  image: IMAGES.accounting,
  alt: 'Accountant and practice manager reviewing reports together',
  pain: 'Reporting periods and secure client data leave no room for outages or slow support.',
  outcome: 'Reliable technology for reporting periods, practice applications, secure client data and distributed teams.'
}];
const secondary = [{
  name: 'Engineering',
  icon: HardHat,
  accent: 'var(--svc-purple-deep)',
  tint: 'rgba(87,87,142,0.06)',
  body: 'Infrastructure, collaboration and security for project teams working across offices, sites and specialist systems.'
}, {
  name: 'Health',
  icon: HeartPulse,
  accent: 'var(--svc-green-deep)',
  tint: 'rgba(10,167,126,0.06)',
  body: 'Dependable, secure technology for practices where availability, privacy and responsive support matter every day.'
}];
const primaryIcons = {
  Legal: Scale,
  Accounting: Calculator
} as const;
export function Industries() {
  return <Section id="industries" className="bg-white" ariaLabel="Industries" data-id="element-248">
      <SectionHeading eyebrow="Built around the way professional firms work" title="Sector knowledge changes the quality of the advice." data-id="element-249" />

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-250">
        {primary.map(ind => {
        const Icon = primaryIcons[ind.name as keyof typeof primaryIcons];
        return <article key={ind.name} className="group flex flex-col overflow-hidden rounded-panel border border-border bg-white shadow-sm transition-all duration-300 ease-smooth hover:-translate-y-1.5 hover:shadow-lg" data-id="element-251">
              <div className="relative h-60 overflow-hidden md:h-72" data-id="element-252">
                <img src={ind.image} alt={ind.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-[1.06]" data-id="element-253" />
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(180deg, rgba(6,24,43,0) 40%, rgba(6,24,43,0.65) 100%)'
            }} aria-hidden data-id="element-254" />
                <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-display text-[15px] font-bold text-navy shadow-sm backdrop-blur" data-id="element-255">
                  <Icon className="h-4 w-4 text-ct-deep" aria-hidden data-id="element-256" />
                  {ind.name}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-7" data-id="element-257">
                <p className="text-[14px] font-semibold uppercase tracking-wide text-security/80" data-id="element-258">
                  The pressure
                </p>
                <p className="mt-1.5 text-[15px] leading-relaxed text-slate" data-id="element-259">
                  {ind.pain}
                </p>
                <p className="mt-5 text-[14px] font-semibold uppercase tracking-wide text-managed-deep" data-id="element-260">
                  With CT Group
                </p>
                <p className="mt-1.5 text-[15px] leading-relaxed text-ink" data-id="element-261">
                  {ind.outcome}
                </p>
                <a href="#" className="mt-auto inline-flex items-center gap-1.5 pt-6 text-[15px] font-bold text-ct-deep transition-colors hover:text-ct-cyan" data-id="element-262">
                  Explore IT for {ind.name}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden data-id="element-263" />
                </a>
              </div>
            </article>;
      })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-264">
        {secondary.map(ind => <article key={ind.name} className="group flex flex-col justify-between rounded-card border border-border bg-white p-7 shadow-sm transition-all duration-300 ease-smooth hover:-translate-y-1 hover:shadow-md" data-id="element-265">
            <div data-id="element-266">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{
            background: ind.tint
          }} data-id="element-267">
                <ind.icon className="h-6 w-6" style={{
              color: ind.accent
            }} aria-hidden data-id="element-268" />
              </span>
              <h3 className="mt-5 font-display text-[20px] font-bold text-navy" data-id="element-269">
                {ind.name}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate" data-id="element-270">
                {ind.body}
              </p>
            </div>
            <a href="#" className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-bold transition-colors" style={{
          color: ind.accent
        }} data-id="element-271">
              Explore IT for {ind.name}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden data-id="element-272" />
            </a>
          </article>)}
      </div>
    </Section>;
}